import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { AuthorizationGroupPasswords } from "@/services/partnerApi"
import type { AuthorizationGroupPassword } from "@/services/partnerApi"

const authGroupPasswordQueryKeys = {
  all: ["authGroupPasswords"] as const,
  detail: (id: string) =>
    [...authGroupPasswordQueryKeys.all, "detail", id] as const,
}

export function useAuthGroupPasswords(authGroupId?: string) {
  return useQuery({
    queryKey: authGroupPasswordQueryKeys.all,

    staleTime: 1000 * 60 * 5,

    queryFn: async () => {
      let passwords: Array<AuthorizationGroupPassword> = []
      const pagination = { offset: 0, limit: 200 }

      while (true) {
        const response =
          await AuthorizationGroupPasswords.ListAuthorizationGroupPasswords({
            pagination,
          })
        if (response.authorization_group_passwords.length === 0) {
          break
        }
        passwords = passwords.concat(response.authorization_group_passwords)
        if (response.authorization_group_passwords.length < pagination.limit) {
          break
        }
        pagination.offset += response.authorization_group_passwords.length
      }

      return passwords
    },

    select: authGroupId
      ? (passwords) =>
          passwords.filter((p) => p.authorization_group_id === authGroupId)
      : (passwords) => passwords,
  })
}

export function useAuthGroupPassword(id: string) {
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: authGroupPasswordQueryKeys.detail(id),

    // The generated response types this field optional. Returning it straight
    // through means handing react-query an undefined, which it rejects with
    // "Query data cannot be undefined" and no clue which record was missing.
    queryFn: async () => {
      const { authorization_group_password: password } =
        await AuthorizationGroupPasswords.GetAuthorizationGroupPassword({ id })
      if (!password) {
        throw new Error(`No authorization group password returned for id ${id}`)
      }
      return password
    },

    initialData: () =>
      queryClient
        .getQueryData<
          Array<AuthorizationGroupPassword>
        >(authGroupPasswordQueryKeys.all)
        ?.find((s) => s.id === id),

    initialDataUpdatedAt: () =>
      queryClient.getQueryState(authGroupPasswordQueryKeys.all)?.dataUpdatedAt,
  })
}

export function useCreateAuthGroupPassword() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: AuthorizationGroupPasswords.CreateAuthorizationGroupPassword,

    onMutate: async (newAuthGroupPassword) => {
      await queryClient.cancelQueries({
        queryKey: authGroupPasswordQueryKeys.all,
      })
      const previous = queryClient.getQueryData(authGroupPasswordQueryKeys.all)
      queryClient.setQueryData<Array<AuthorizationGroupPassword>>(
        authGroupPasswordQueryKeys.all,
        (old) => {
          const password = { ...newAuthGroupPassword, id: "new" }
          return old ? [...old, password] : [password]
        },
      )
      return { previous }
    },

    onError: (_err, _newAuthGroupPassword, context) => {
      if (context) {
        queryClient.setQueryData(
          authGroupPasswordQueryKeys.all,
          context.previous,
        )
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: authGroupPasswordQueryKeys.all,
      })
    },
  })
}

export function useUpdateAuthGroupPassword() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: AuthorizationGroupPasswords.UpdateAuthorizationGroupPassword,

    onMutate: async (newAuthGroup) => {
      await queryClient.cancelQueries({
        queryKey: authGroupPasswordQueryKeys.all,
      })
      const previous = queryClient.getQueryData(authGroupPasswordQueryKeys.all)
      queryClient.setQueryData<Array<AuthorizationGroupPassword>>(
        authGroupPasswordQueryKeys.all,
        (old) => {
          return old?.map((ag) =>
            ag.id === newAuthGroup.id ? newAuthGroup : ag,
          )
        },
      )
      return { previous }
    },

    onError: (_err, _newAuthGroup, context) => {
      if (context) {
        queryClient.setQueryData(
          authGroupPasswordQueryKeys.all,
          context.previous,
        )
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: authGroupPasswordQueryKeys.all,
      })
    },
  })
}

export function useDeleteAuthGroupPassword() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: AuthorizationGroupPasswords.DeleteAuthorizationGroupPassword,

    onMutate: async (authGroup) => {
      await queryClient.cancelQueries({
        queryKey: authGroupPasswordQueryKeys.all,
      })
      const previous = queryClient.getQueryData(authGroupPasswordQueryKeys.all)
      queryClient.setQueryData<Array<AuthorizationGroupPassword>>(
        authGroupPasswordQueryKeys.all,
        (old) => {
          return old?.filter((s) => s.id !== authGroup.id)
        },
      )
      return { previous }
    },

    onError: (_err, _authGroup, context) => {
      if (context) {
        queryClient.setQueryData(
          authGroupPasswordQueryKeys.all,
          context.previous,
        )
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: authGroupPasswordQueryKeys.all,
      })
    },
  })
}
